import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { ethers } from 'ethers';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNetworkByChainId } from '../config/networks';

interface ContractContextType {
  contractAddress: string;
  chainId: string;
  setContractAddress: (address: string, chainId?: string) => void;
  clearContractAddress: () => void;
  isValidAddress: (address: string) => boolean;
  isAddressSet: boolean;
}

const ContractContext = createContext<ContractContextType | undefined>(undefined);

export const useContract = () => {
  const context = useContext(ContractContext);
  if (context === undefined) {
    throw new Error('useContract must be used within a ContractProvider');
  }
  return context;
};

interface ContractProviderProps {
  children: ReactNode;
}

export const ContractProvider: React.FC<ContractProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract query parameters
  const searchParams = new URLSearchParams(location.search);
  const contractAddress = searchParams.get('contractAddress') || '';
  const chainId = searchParams.get('chainId') || '';

  const setContractAddress = useCallback((address: string, newChainId?: string) => {
    // Validate address
    if (!address || !ethers.isAddress(address)) {
      console.error('Invalid contract address provided');
      navigate('/');
      return;
    }
    
    // Determine and validate chainId
    const targetChainId = newChainId || chainId;
    if (!targetChainId) {
      console.error('No chain ID provided or available in URL');
      navigate('/');
      return;
    }
    
    // Validate chainId is a valid positive number
    const parsedChainId = parseInt(targetChainId, 10);
    if (isNaN(parsedChainId) || parsedChainId <= 0) {
      console.error('Invalid chain ID:', targetChainId);
      navigate('/');
      return;
    }
    
    // Validate chainId is a supported network
    const network = getNetworkByChainId(parsedChainId);
    if (!network) {
      console.error('Unsupported chain ID:', parsedChainId);
      navigate('/');
      return;
    }
    
    // Update URL with query parameters
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('chainId', targetChainId);
    newSearchParams.set('contractAddress', address);
    navigate(`/?${newSearchParams.toString()}`);
  }, [chainId, navigate]);

  const clearContractAddress = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const isValidAddress = useCallback((address: string) => {
    return ethers.isAddress(address);
  }, []);

  const isAddressSet = contractAddress !== '';

  const value: ContractContextType = {
    contractAddress,
    chainId,
    setContractAddress,
    clearContractAddress,
    isValidAddress,
    isAddressSet,
  };

  return (
    <ContractContext.Provider value={value}>
      {children}
    </ContractContext.Provider>
  );
};
